import React, { useMemo, useState } from 'react';

import { Badge, Popover } from '@mui/material';
import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { DropdownSelectButton } from 'src/components';
import { makeStyles } from 'tss-react/mui';

import {
  AssigneeFilterContent,
  BoardFilterContent,
  ColumnFilterContent,
  LabelsFilterContent,
  PriorityFilterContent,
  RequesterFilterContent,
  SigningStatusFilterContent,
  StatusFilterContent,
  WorkflowTypeFilterContent,
} from './filter-contents';
import type { WorkflowFilterConfig } from './workflow-filter-config';

const useStyles = makeStyles()(theme => ({
  filterBadge: {
    '& .MuiBadge-badge': {
      right: 2,
      top: 2,
    },
  },
  popoverContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    padding: theme.spacing(1),
  },
  popoverPaper: {
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    maxHeight: 250,
    minWidth: 190,
    padding: theme.spacing(1, 0),
  },
}));

interface FilterOptionProps {
  filter: WorkflowFilterConfig;
}

function getActiveFilterCount(value: unknown): number {
  if (!Array.isArray(value)) return 0;
  return value.length;
}

export function FilterOption({ filter }: FilterOptionProps): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const [isOpen, setIsOpen] = useState(false);
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const activeCount = useMemo(() => {
    const filterItem = filterModel.items.find(item => item.field === filter.field);
    return getActiveFilterCount(filterItem?.value);
  }, [filter.field, filterModel.items]);

  return (
    <>
      <Badge badgeContent={activeCount} className={classes.filterBadge} color="primary">
        <DropdownSelectButton
          ref={setAnchorEl}
          icon={filter.icon}
          label={filter.label}
          selected={isOpen}
          variant="outlined"
          onClick={() => setIsOpen(!isOpen)}
        />
      </Badge>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        open={isOpen}
        slotProps={{ paper: { className: classes.popoverPaper } }}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
        onClose={() => setIsOpen(false)}
      >
        <div className={classes.popoverContent}>
          <FilterContent filter={filter} />
        </div>
      </Popover>
    </>
  );
}

function FilterContent({ filter }: { filter: WorkflowFilterConfig }): JSX.Element {
  switch (filter.type) {
    case 'board':
      return <BoardFilterContent {...filter} />;
    case 'column':
      return <ColumnFilterContent {...filter} />;
    case 'priority':
      return <PriorityFilterContent {...filter} />;
    case 'workflowType':
      return <WorkflowTypeFilterContent {...filter} />;
    case 'requester':
      return <RequesterFilterContent {...filter} />;
    case 'signingStatus':
      return <SigningStatusFilterContent {...filter} />;
    case 'status':
      return <StatusFilterContent {...filter} />;
    case 'labels':
      return <LabelsFilterContent {...filter} />;
    case 'assignee':
      return <AssigneeFilterContent {...filter} />;
  }
}
